import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserAvatar } from "@/components/UserAvatar";
import { fullName, memberTypeColor } from "@/lib/format";
import { Linkedin } from "lucide-react";

export default function Directory() {
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ colleges: [], member_types: [] });
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [college, setCollege] = useState("all");
  const [memberType, setMemberType] = useState("all");
  const [gradYear, setGradYear] = useState("all");

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (college !== "all") params.college = college;
    if (memberType !== "all") params.member_type = memberType;
    if (gradYear !== "all") params.grad_year = parseInt(gradYear, 10);
    setLoading(true);
    api.get("/users", { params })
      .then(({ data }) => setUsers(data))
      .finally(() => setLoading(false));
  }, [q, college, memberType, gradYear]);

  const gradYears = useMemo(() => {
    return Array.from({ length: 2035 - 1960 + 1 }, (_, i) => 2035 - i);
  }, []);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Directory</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {users.length} Cornellians in Northeast Ohio.
        </p>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-border cornell-shadow p-4 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            placeholder="Search by name, bio, college…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="directory-search-input"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Select value={college} onValueChange={setCollege}>
            <SelectTrigger data-testid="directory-college-filter"><SelectValue placeholder="College" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="directory-college-all">All colleges</SelectItem>
              {meta.colleges.map((c) => (
                <SelectItem key={c} value={c} data-testid={`directory-college-${c.replace(/\s+/g, "-")}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={memberType} onValueChange={setMemberType}>
            <SelectTrigger data-testid="directory-member-type-filter"><SelectValue placeholder="Member type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="directory-member-type-all">All members</SelectItem>
              {meta.member_types.map((t) => (
                <SelectItem key={t} value={t} data-testid={`directory-member-type-${t.replace(/\s+/g, "-")}`}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={gradYear} onValueChange={setGradYear}>
            <SelectTrigger data-testid="directory-grad-year-filter"><SelectValue placeholder="Grad year" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all" data-testid="directory-grad-year-all">All years</SelectItem>
              {gradYears.map((y) => (
                <SelectItem key={y} value={String(y)} data-testid={`directory-grad-year-${y}`}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="directory-loading">Loading members…</div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-white rounded-2xl border border-border" data-testid="directory-empty">
          No members match your filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="directory-list">
          {users.map((u) => (
            <Link
              key={u.id}
              to={`/profile/${u.id}`}
              className="bg-white rounded-2xl border border-border p-4 flex items-start gap-3 cornell-shadow-hover"
              data-testid={`directory-user-${u.id}`}
            >
              <UserAvatar user={u} size={52} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold truncate">{fullName(u)}</span>
                  {u.role === "admin" && (
                    <span className="text-[9px] uppercase tracking-wider bg-[#B31B1B] text-white px-1.5 py-0.5 rounded">Admin</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{u.college} · '{String(u.grad_year).slice(-2)}</div>
                <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${memberTypeColor(u.member_type)}`}>{u.member_type}</span>
                  {u.linkedin_url && (
                    <span className="inline-flex items-center text-neutral-400"><Linkedin className="w-3 h-3" /></span>
                  )}
                </div>
                {u.bio && <p className="mt-2 text-xs text-neutral-600 line-clamp-2">{u.bio}</p>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
